import React, { useState } from 'react';
import { Button, Card, Flex, Typography, theme } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

interface FoldSectionProps {
  /** Якорь навигации шапки — висит на секции в обоих состояниях. */
  id: string;
  title: React.ReactNode;
  /** Что видно в свёрнутом виде: итог блока, а не пустая плашка. */
  summary: React.ReactNode;
  defaultOpen?: boolean;
  /** Вид «вкладки»: блок и так открыт на всю страницу, сворачивать нечего. */
  forceOpen?: boolean;
  children: React.ReactNode;
}

/**
 * Блок страницы расчёта, свёрнутый по умолчанию до своей сводки (фидбек 29.09:
 * «свернём, но убирать совсем нельзя»).
 *
 * Содержимое НЕ размонтируется, а прячется: бюджет и прогноз отдают свои данные
 * наверх (`onDataChange`, `onSummaryChange`), и из них считаются результат и
 * «Итого». Размонтированный бюджет молча вернул бы в расчёт бюджет по умолчанию.
 */
const FoldSection: React.FC<FoldSectionProps> = ({ id, title, summary, defaultOpen = false, forceOpen = false, children }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [openState, setOpen] = useState(defaultOpen);
  const open = forceOpen || openState;

  return (
    <section id={id} style={{ scrollMarginTop: 72 }}>
      {!open && (
        <Card variant="outlined" size="small" styles={{ body: { padding: `${token.paddingSM}px ${token.padding}px` } }}>
          <Flex align="center" gap="middle" wrap>
            <Typography.Text strong style={{ fontSize: token.fontSizeLG }}>{title}</Typography.Text>
            <div style={{ flex: '1 1 auto', minWidth: 0 }}>{summary}</div>
            <Button icon={<DownOutlined />} onClick={() => setOpen(true)} aria-expanded={false}>
              {t('idea_calc.fold.show')}
            </Button>
          </Flex>
        </Card>
      )}
      {open && !forceOpen && (
        <Flex justify="flex-end" style={{ marginBottom: token.marginXS }}>
          <Button size="small" type="link" icon={<UpOutlined />} onClick={() => setOpen(false)} aria-expanded>
            {t('idea_calc.fold.hide')}
          </Button>
        </Flex>
      )}
      <div hidden={!open}>{children}</div>
    </section>
  );
};

export default FoldSection;
